import dayjs from "dayjs";
import { externalgetApi } from "../../services/external/externalApis";
import {
  ORDER_API_14,
  ORDER_API_15,
  ORDER_API_17,
} from "../../api/ApiModel";
import {
  CON_ORTC_EXCHANGE,
  CON_ORTC_RETURN,
  CON_STATUS_DELETED,
} from "../sys/Constants";
import {
  getNoteRefundStatus,
  isNoteClaimed,
  isNoteExpired,
} from "../order/noteRefundStatus";

const isCreditOrExchange = (returnTypeCode) => {
  const code = Number(returnTypeCode);
  return code === CON_ORTC_RETURN || code === CON_ORTC_EXCHANGE;
};

const buildNoteResult = (listRow, hdr) => {
  const header = hdr || listRow || {};
  const rtCode = Number(header.RETURNTYPECODE ?? listRow?.RETURNTYPECODE);
  if (!isCreditOrExchange(rtCode)) {
    return { error: "invalid_type" };
  }

  const amount =
    parseFloat(String(header.REFUNDAMOUNT ?? listRow?.REFUNDAMOUNT).replace(/,/g, "")) || 0;
  const refundSts = header.REFUNDSTS ?? listRow?.REFUNDSTS;
  const returnDate = header.RETURNDATE ?? listRow?.RETURNDATE;
  const statusRow = { REFUNDSTS: refundSts, RETURNDATE: returnDate };
  const refundStatus = getNoteRefundStatus(statusRow);
  const isClaimed = isNoteClaimed(refundSts);
  const isExpired = isNoteExpired(returnDate);
  const customerName =
    listRow?.OFFICERNAME ||
    listRow?.GUESTNAME ||
    listRow?.CUSTOMERNAME ||
    header?.OFFICERNAME ||
    "";

  return {
    note: {
      odreturncode: header.ODRETURNCODE ?? listRow?.ODRETURNCODE,
      returnno: header.RETURNNO ?? listRow?.RETURNNO,
      ordercode: header.ORDERCODE ?? listRow?.ORDERCODE,
      returnTypeCode: rtCode,
      noteTypeLabel:
        rtCode === CON_ORTC_EXCHANGE ? "Exchange Note" : "Credit Note",
      amount,
      customerName: String(customerName).trim() || null,
      returnDate,
      refundSts,
      refundStatusLabel: refundStatus.label,
      isClaimed,
      isExpired,
      isRefunded: isClaimed,
      isRefundable: !isClaimed && !isExpired,
      listRecord: listRow,
      headerRecord: header,
    },
  };
};

const normalizeSearchResponse = (res, key) => {
  if (!res) return null;
  const rows = Array.isArray(res) ? res : [res];
  const match =
    rows.find(
      (r) =>
        String(r.ODRETURNCODE) === key ||
        String(r.RETURNNO) === key
    ) || (rows.length === 1 ? rows[0] : null);
  if (!match || !isCreditOrExchange(match.RETURNTYPECODE)) return null;
  return buildNoteResult(match, match);
};

/**
 * Search credit/exchange note by note id (ODRETURNCODE or RETURNNO).
 */
export async function searchCreditNote({ noteId, COMPCODE, LOCCODE, RESTCODE }) {
  const key = String(noteId || "").trim();
  if (!key) {
    return { error: "empty" };
  }

  try {
    const list = await externalgetApi(ORDER_API_14, "GET", {
      COMPCODE,
      LOCCODE,
      RESTCODE,
      FROMDATE: dayjs().startOf("month").format("MM-DD-YYYY"),
      TODATE: dayjs().endOf("month").format("MM-DD-YYYY"),
      STSD: CON_STATUS_DELETED,
    });
    const records = Array.isArray(list) ? list : [];
    const match = records.find(
      (r) =>
        isCreditOrExchange(r.RETURNTYPECODE) &&
        (String(r.ODRETURNCODE) === key || String(r.RETURNNO) === key)
    );
    if (!match) {
      return { error: "not_found" };
    }

    try {
      const detail = await externalgetApi(ORDER_API_15, "get", {
        ORDERCODE: match.ORDERCODE,
        ODRETURNCODE: match.ODRETURNCODE,
      });
      const hdr = Array.isArray(detail?.["4"]) ? detail["4"][0] : null;
      return buildNoteResult(match, hdr);
    } catch {
      return buildNoteResult(match, match);
    }
  } catch {
    return { error: "failed" };
  }
}
